// Key prefix to put at the start of every data entry in the local storage.
import { LOCAL_STORAGE_PREFIX } from '../constants/local_storage_namespace';



/**
 * Inserts data inside the browser's local storage.
 * 
 * @function putInLocalStorage
 * 
 * @param {String} [key=''] - an identifier to the data that's going to be inserted.
 * @param {Boolean} single - if the data is an object, whether or not to insert each item in a separate entry.
 * @param {*} data - something to put inside the localStorage.
 */
export const putInLocalStorage =
    (key = '') => (single) => (data) => {
        if (!key && single)
            throw new Error('No key provided to a single localStorage insertion on putInLocalStorage.');
        else if (!single && (typeof data !== 'object' || Array.isArray(data)))
            throw new Error('Wrong data argument type passed to putInLocalStorage: data must be an object.');

        if (single)
            localStorage.setItem(`${key}`, formatDataToInsert(data));
        else
            Object.keys(data).forEach((k) => localStorage.setItem(
                `${LOCAL_STORAGE_PREFIX}${key}${k}`,
                formatDataToInsert(data[k])
            ));
    }



/**
 * Fetch data from the browser's local storage.
 * 
 * @function getFromLocalStorage
 * 
 * @param {String} key - a string to be concatenated with the LOCAL_STORAGE_PREFIX in order to get data from
 *      the local storage.
 */
export const getFromLocalStorage =
    (key) => key.startsWith(LOCAL_STORAGE_PREFIX)
        ? localStorage.getItem(key)
        : localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${key}`);



/**
 * Continuously tries to get a given data from the local storage.
 * 
 * @param {String} key - the local storage key that holds the data
 * @param {Number} [timeout] - timeout to reject the Promise
 * 
 * @returns {Promise} - returns a Promise that resolves with the data fetched from the local storage.
 */
export const getFromLocalStorageAsync =
    (key, timeout) => new Promise((resolve, reject) => {
        // Mark the start time of the function call.
        const startTime = new Date().getTime();

        // Tries to load the data before setting the interval
        const preFetchedData = localStorage.getItem(key);

        // If the data exists, just return it.
        if (preFetchedData !== null)
            return resolve(preFetchedData);


        // Create an interval to fetch the data from the local storage.
        const fetchInterval = window.setInterval(() => {
            // Tries to fetch from the local storage.
            const data = localStorage.getItem(key);

            if (data !== null) {
                // Clear the interval function when the data was found.
                window.clearInterval(fetchInterval);

                // Resolves the Promise with the data found.
                return resolve(data);
            }

            // If there was a timeout defined and there has been enough time passed, reject the Promise.
            if (timeout && new Date().getTime() - startTime >= timeout) {
                // Clear the interval so the effect doesn't propagate.
                window.clearInterval(fetchInterval);
                return reject();
            }

        }, 500);
    });



/**
 * Formats data passed into the function so it can be injected into the local storage.
 * 
 * @function formatDataToInsert
 * 
 * @param {*} data - any data to be inserted into the local storage.
 * 
 * @returns {*} the formatted data passed.
 */
const formatDataToInsert =
    (data) => typeof data === 'object'
        ? JSON.stringify(data)
        : data;


export const getCompanyList = () =>
    JSON.parse(getFromLocalStorage('company_list'));

export const getCustomerList = () =>
    JSON.parse(getFromLocalStorage('person_list'));

export const getAgencyList = () =>
    JSON.parse(getFromLocalStorage('agency_list'));

export const getStatusCodes = () =>
    JSON.parse(getFromLocalStorage('booking_status_codes'));
